import { useEffect, useRef, useState } from "react";
import { Progress } from "@/components/circle/Parts";
import { StepOne, StepThree, StepTwo } from "@/components/circle/Steps";
import Success from "@/components/circle/Success";
import { attribution, circleApi, errorText, TOKEN_KEY } from "@/lib/circle";
import { track } from "@/lib/site";

const EMPTY = { first_name: "", last_name: "", email: "", country: "", privacy_ack: false, interest: "", amount_range: "", communication: "", notes: "", marketing_consent: false, website: "" };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

function check1(f) {
    if (!f.first_name.trim() || !f.last_name.trim()) return "Please enter your first and last name.";
    if (!EMAIL_RE.test(f.email.trim())) return "Please enter a valid email address.";
    if (!f.country) return "Please select your country of residence.";
    if (!f.privacy_ack) return "Please confirm you have read the privacy notice.";
    return "";
}

export default function CircleForm() {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [resumed, setResumed] = useState("");
    const box = useRef(null);
    const started = useRef(false);
    const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setError(""); };

    useEffect(() => {
        if (!localStorage.getItem(TOKEN_KEY)) return;
        circleApi.session().then((d) => {
            setForm((p) => ({ ...p, first_name: d.first_name, last_name: d.last_name, email: d.email, country: d.country, privacy_ack: true, interest: d.interest, amount_range: d.amount_range }));
            setResumed(d.first_name);
            setStep(d.step);
        }).catch(() => localStorage.removeItem(TOKEN_KEY));
    }, []);

    const go = (n) => {
        setStep(n);
        setError("");
        requestAnimationFrame(() => {
            box.current?.querySelector("[data-ic-heading]")?.focus({ preventScroll: true });
            const top = box.current?.getBoundingClientRect().top;
            if (top !== undefined && top < 80) window.scrollTo({ top: window.scrollY + top - 96, behavior: "smooth" });
        });
    };

    const run = async (fn) => {
        setBusy(true);
        try { await fn(); } catch (err) {
            if (err?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setResumed(""); go(1); }
            setError(errorText(err));
        } finally { setBusy(false); }
    };

    const submit1 = (e) => {
        e.preventDefault();
        const msg = check1(form);
        if (msg) { setError(msg); return; }
        run(async () => {
            const d = await circleApi.start({ ...form, ...attribution() });
            if (d.already_registered) { go(4.5); return; }
            localStorage.setItem(TOKEN_KEY, d.token);
            track("ic_step1_completed");
            go(2);
        });
    };
    const submit2 = (e) => {
        e.preventDefault();
        if (!form.interest || !form.amount_range) { setError("Please choose one option for each question."); return; }
        run(async () => { await circleApi.prefs({ interest: form.interest, amount_range: form.amount_range }); track("ic_step2_completed"); go(3); });
    };
    const submit3 = (e) => {
        e.preventDefault();
        if (!form.communication) { setError("Please choose what you'd like to receive."); return; }
        if (!form.marketing_consent) { setError("Please tick the box to receive our emails."); return; }
        run(async () => {
            await circleApi.complete({ communication: form.communication, notes: form.notes, marketing_consent: true });
            localStorage.removeItem(TOKEN_KEY);
            track("ic_registration_completed");
            go(4);
        });
    };
    const onFocus = () => { if (!started.current) { started.current = true; track("ic_registration_started"); } };
    const restart = () => { localStorage.removeItem(TOKEN_KEY); setResumed(""); setForm(EMPTY); go(1); };

    const props = { form, set, busy, error };
    return (
        <div ref={box} onFocusCapture={onFocus} data-testid="ic-form" className="rounded-[1.75rem] bg-cream text-ink p-6 sm:p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.45)]">
            {step <= 3 && <Progress step={step} />}
            {resumed && step <= 3 && (
                <p className="-mt-3 mb-6 text-sm text-muted_text" data-testid="ic-resume-note">Welcome back, {resumed}. <button type="button" onClick={restart} className="underline hover:text-forest" data-testid="ic-restart">Not you? Start over</button></p>
            )}
            <div key={step} className="ic-step">
                {step === 1 && <StepOne {...props} onSubmit={submit1} />}
                {step === 2 && <StepTwo {...props} onSubmit={submit2} onBack={() => go(1)} />}
                {step === 3 && <StepThree {...props} onSubmit={submit3} onBack={() => go(2)} />}
                {step === 4 && <Success />}
                {step === 4.5 && <Success already />}
            </div>
        </div>
    );
}
